import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CalendarDays, ChevronLeft, ChevronRight, Copy, LayoutGrid, List, Plus, Rows3, Sun, Users } from 'lucide-react';
import { useAuth } from '../../context/authcontext';
import { usePermission } from '../../hooks/usePermission';
import { createPermissionString as perm } from '../../types/permissions';
import { useMobileDetection } from '../../hooks/useMobileUtils';
import { useToast } from '../common/Toast';
import Dialog from '../common/Dialog';
import { resolveAgencyTimezone, toAgencyDate } from '../../utils/agencyTime';
import { ScheduleService, scheduleErrorMessage } from '../../services/scheduling';
import type { UserData } from '../../dtos/admin/user.dto';
import { DISCIPLINES, Shift } from '../../dtos/scheduling/schedule.dto';
import TodayBoard from './TodayBoard';
import CoverageWeek from './CoverageWeek';
import ClinicianWeek from './ClinicianWeek';
import MonthView from './MonthView';
import AgendaList from './AgendaList';
import ShiftDrawer, { ShiftDrawerProps } from './ShiftDrawer';
import { useNavigate } from 'react-router-dom';
import { visitWorkspacePath } from '../offline/workspace/VisitWorkspaceRoute';
import CancelShiftDialog from './CancelShiftDialog';
import BulkBar from './BulkBar';
import Legend from './Legend';
import {
  addDays, addMonths, dayLabel, longDateLabel, monthGrid, monthLabel, rangeLabel, shiftWindow, weekDays, weekStart,
} from './scheduleUtils';

export type ScheduleView = 'today' | 'coverage' | 'clinicians' | 'month' | 'list';

const VIEWS: { id: ScheduleView; label: string; icon: React.ReactNode; desktopOnly?: boolean }[] = [
  { id: 'today', label: 'Today', icon: <Sun size={16} /> },
  { id: 'coverage', label: 'Coverage', icon: <Rows3 size={16} /> },
  { id: 'clinicians', label: 'Clinicians', icon: <Users size={16} />, desktopOnly: true },
  { id: 'month', label: 'Month', icon: <LayoutGrid size={16} />, desktopOnly: true },
  { id: 'list', label: 'List', icon: <List size={16} /> },
];

export interface ScheduleShellProps {
  /** From the sidebar: 'Today' or 'Week' */
  initialView?: ScheduleView;
}

const isView = (v: string | null): v is ScheduleView => !!v && VIEWS.some((x) => x.id === v);

/**
 * Schedule (Phase 9A, plan §6). Coordinators get Today, Coverage, Clinicians, Month and List;
 * clinicians without all-patient access get their own list (SCH-08). The view, date and filters
 * live in the URL, so a view can be bookmarked or shared. Keys: N new · T today · ←/→ move.
 */
const ScheduleShell: React.FC<ScheduleShellProps> = ({ initialView }) => {
  const { agencyTimezone } = useAuth();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const isMobile = useMobileDetection();
  const canReadAll = usePermission(perm('scheduling:phi:readall'));
  const canWrite = usePermission([perm('scheduling:phi:create'), perm('scheduling:phi:createall')]);
  const canBulk = usePermission(perm('scheduling:phi:updateall'));
  const today = useMemo(() => toAgencyDate(new Date(), resolveAgencyTimezone(agencyTimezone)), [agencyTimezone]);

  const [params, setParams] = useSearchParams();
  const urlView = params.get('sv');
  const view: ScheduleView = !canReadAll ? 'list'
    : isView(urlView) ? (isMobile && VIEWS.find((v) => v.id === urlView)?.desktopOnly ? 'list' : urlView)
      : (initialView || 'today');
  const date = params.get('sd') && /^\d{4}-\d{2}-\d{2}$/.test(params.get('sd') || '') ? (params.get('sd') as string) : today;
  const discipline = params.get('sdisc') || '';
  const providerFilter = params.get('sprov') || '';
  const unstaffedOnly = params.get('sopen') === '1';

  const setParam = useCallback((updates: Record<string, string | null>) => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      for (const [k, v] of Object.entries(updates)) {
        if (v === null || v === '') next.delete(k);
        else next.set(k, v);
      }
      return next;
    }, { replace: true });
  }, [setParams]);

  // Sidebar Today / Week clicks move to that view
  useEffect(() => {
    if (initialView && canReadAll) setParam({ sv: initialView });
  }, [initialView, canReadAll, setParam]);

  const range = useMemo(() => {
    if (view === 'month') {
      const grid = monthGrid(date);
      return { from: grid[0], to: grid[41], days: grid };
    }
    if (view === 'list' && !canReadAll) return { from: date, to: addDays(date, 13), days: Array.from({ length: 14 }, (_, i) => addDays(date, i)) };
    const start = weekStart(date);
    return { from: start, to: addDays(start, 6), days: weekDays(start) };
  }, [view, date, canReadAll]);

  const [clinicians, setClinicians] = useState<UserData[]>([]);
  useEffect(() => {
    if (!canWrite && !canReadAll) return;
    ScheduleService.listClinicians().then(setClinicians).catch(() => setClinicians([]));
  }, [canWrite, canReadAll]);

  const [refreshKey, setRefreshKey] = useState(0);
  const refresh = () => setRefreshKey((k) => k + 1);
  const [shifts, setShifts] = useState<Shift[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const needsList = view === 'clinicians' || view === 'month' || view === 'list';

  useEffect(() => {
    if (!needsList) return undefined;
    let live = true;
    setLoading(true);
    const load = canReadAll
      ? ScheduleService.list({ startDate: range.from, endDate: range.to, discipline: discipline || undefined, providerId: providerFilter || undefined, unstaffed: unstaffedOnly })
      : ScheduleService.listByProvider('me', range.from, range.to);
    load.then((items) => { if (live) { setShifts(items); setError(null); } })
      .catch((e) => live && setError(scheduleErrorMessage(e, 'Could not load the schedule')))
      .finally(() => live && setLoading(false));
    return () => { live = false; };
  }, [needsList, canReadAll, range.from, range.to, discipline, providerFilter, unstaffedOnly, refreshKey]);

  // Selection (bulk actions)
  const [selected, setSelected] = useState<Map<string, Shift>>(new Map());
  useEffect(() => { setSelected(new Map()); }, [view, date, refreshKey]);
  const toggleSelect = (s: Shift) => setSelected((prev) => {
    const next = new Map(prev);
    if (next.has(s.id)) next.delete(s.id);
    else if (s.status === 'SCHEDULED') next.set(s.id, s);
    return next;
  });
  const selectedIds = useMemo(() => new Set(selected.keys()), [selected]);

  // Drawer
  const [drawer, setDrawer] = useState<{ open: boolean; shift: Shift | null; prefill?: ShiftDrawerProps['prefill'] }>({ open: false, shift: null });
  const [lastProvider, setLastProvider] = useState<string | null>(null);
  const openNew = useCallback((prefill: ShiftDrawerProps['prefill'] = {}) => {
    if (!canWrite) return;
    setDrawer({ open: true, shift: null, prefill: { date: date < today ? today : date, providerId: lastProvider, ...prefill } });
  }, [canWrite, date, today, lastProvider]);
  const openShift = (s: Shift) => setDrawer({ open: true, shift: s });
  const [cancelTarget, setCancelTarget] = useState<Shift | null>(null);
  const [cancelBusy, setCancelBusy] = useState(false);
  const [copyOpen, setCopyOpen] = useState(false);

  const move = useCallback((dir: -1 | 1) => {
    const next = view === 'month' ? addMonths(date, dir) : view === 'today' ? addDays(date, dir) : addDays(date, dir * 7);
    setParam({ sd: next });
  }, [view, date, setParam]);

  // Keyboard: N new, T today, arrows move (ignored while typing or a dialog is open)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement;
      if (drawer.open || cancelTarget || copyOpen || ['INPUT', 'TEXTAREA', 'SELECT'].includes(t.tagName) || t.isContentEditable) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === 'n' || e.key === 'N') { e.preventDefault(); openNew(); }
      if (e.key === 't' || e.key === 'T') setParam({ sd: null });
      if (e.key === 'ArrowLeft') move(-1);
      if (e.key === 'ArrowRight') move(1);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [drawer.open, cancelTarget, copyOpen, openNew, move, setParam]);

  const heading = view === 'today' ? longDateLabel(date) : view === 'month' ? monthLabel(date) : rangeLabel(range.from, range.to);
  const btn = 'flex min-h-[44px] items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 text-sm font-medium text-gray-700 hover:bg-gray-50 md:min-h-[36px]';

  return (
    <div className="flex min-h-full flex-col bg-gray-50 px-4 py-4 md:px-6">
      <header className="mb-4 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="mr-2 flex items-center gap-2 text-xl font-semibold text-gray-900"><CalendarDays size={22} />{canReadAll ? 'Schedule' : 'My schedule'}</h1>
          {canReadAll && (
            <div role="tablist" aria-label="Schedule view" className="flex overflow-x-auto rounded-md border border-gray-300 bg-white">
              {VIEWS.filter((v) => !(isMobile && v.desktopOnly)).map((v) => (
                <button
                  key={v.id}
                  type="button"
                  role="tab"
                  aria-selected={view === v.id}
                  onClick={() => setParam({ sv: v.id })}
                  className={`flex min-h-[44px] items-center gap-1.5 px-3 text-sm font-medium md:min-h-[36px] ${view === v.id ? 'bg-slate-800 text-white' : 'text-gray-700 hover:bg-gray-50'}`}
                >
                  {v.icon}{v.label}
                </button>
              ))}
            </div>
          )}
          {canWrite && (
            <button type="button" onClick={() => openNew()} className="ml-auto flex min-h-[44px] items-center gap-1.5 rounded-md bg-slate-800 px-4 text-sm font-semibold text-white hover:bg-slate-900 md:min-h-[36px]" title="New shift (N)">
              <Plus size={16} />New shift
            </button>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center">
            <button type="button" aria-label="Previous" onClick={() => move(-1)} className="flex h-11 w-11 items-center justify-center rounded-md hover:bg-gray-200 md:h-9 md:w-9"><ChevronLeft size={18} /></button>
            <button type="button" onClick={() => setParam({ sd: null })} className={btn}>Today</button>
            <button type="button" aria-label="Next" onClick={() => move(1)} className="flex h-11 w-11 items-center justify-center rounded-md hover:bg-gray-200 md:h-9 md:w-9"><ChevronRight size={18} /></button>
          </div>
          <span className="text-base font-semibold text-gray-800">{heading}</span>
          {canReadAll && view !== 'today' && view !== 'coverage' && (
            <div className="flex flex-wrap items-center gap-2 md:ml-4">
              <select aria-label="Discipline" value={discipline} onChange={(e) => setParam({ sdisc: e.target.value })} className="min-h-[44px] rounded-md border border-gray-300 bg-white px-2 text-sm md:min-h-[36px]">
                <option value="">All disciplines</option>
                {DISCIPLINES.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
              <select aria-label="Clinician" value={providerFilter} onChange={(e) => setParam({ sprov: e.target.value })} className="min-h-[44px] max-w-[12rem] rounded-md border border-gray-300 bg-white px-2 text-sm md:min-h-[36px]">
                <option value="">All clinicians</option>
                {clinicians.map((c) => <option key={c.id} value={c.id}>{c.firstName} {c.lastName}</option>)}
              </select>
              <label className="flex min-h-[44px] items-center gap-2 text-sm text-gray-700 md:min-h-[36px]">
                <input type="checkbox" className="h-4 w-4" checked={unstaffedOnly} onChange={(e) => setParam({ sopen: e.target.checked ? '1' : null })} />Unstaffed only
              </label>
            </div>
          )}
          {canBulk && (view === 'coverage' || view === 'clinicians') && (
            <button type="button" onClick={() => setCopyOpen(true)} className={`${btn} md:ml-auto`}><Copy size={16} />Copy last week</button>
          )}
        </div>
        {canReadAll && <Legend />}
      </header>

      <main className="flex-1">
        {error && needsList && <div role="alert" className="mb-3 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">{error} <button type="button" className="ml-2 underline" onClick={refresh}>Retry</button></div>}
        {loading && needsList && <div className="mb-2 text-sm text-gray-500">Loading…</div>}
        {view === 'today' && (
          <TodayBoard date={date} isMobile={isMobile} refreshKey={refreshKey} onOpen={openShift} onAssign={openShift} onOpenVisit={(patientId, visitId) => navigate(visitWorkspacePath(visitId, patientId))} />
        )}
        {view === 'coverage' && (
          <CoverageWeek
            weekStart={range.from}
            today={today}
            isMobile={isMobile}
            refreshKey={refreshKey}
            selectedIds={selectedIds}
            onToggleSelect={toggleSelect}
            onOpen={openShift}
            onCreate={(patientId, patientLabel, d) => openNew({ patientId, patientLabel, date: d })}
          />
        )}
        {view === 'clinicians' && !error && (
          <ClinicianWeek shifts={shifts} days={range.days} today={today} selectedIds={selectedIds} onToggleSelect={toggleSelect} onOpen={openShift} />
        )}
        {view === 'month' && !error && (
          <MonthView anchor={date} today={today} shifts={shifts} onOpen={openShift} onDay={(d) => setParam({ sv: 'list', sd: d })} />
        )}
        {view === 'list' && !error && (
          <AgendaList
            days={range.days}
            shifts={shifts}
            today={today}
            primary="patient"
            selectedIds={canBulk ? selectedIds : undefined}
            onToggleSelect={canBulk ? toggleSelect : undefined}
            onOpen={canWrite ? openShift : undefined}
            emptyText={canReadAll ? 'No shifts this week.' : 'You have no shifts in the next 2 weeks.'}
          />
        )}
        {canBulk && selected.size > 0 && (
          <div className="mt-3">
            <BulkBar selected={[...selected.values()]} clinicians={clinicians} today={today} onClear={() => setSelected(new Map())} onDone={refresh} />
          </div>
        )}
        {canBulk && selected.size === 0 && (view === 'coverage' || view === 'clinicians') && !isMobile && (
          <p className="mt-3 text-xs text-gray-500">Tip: Shift-click shifts to select several, then reassign or cancel them together.</p>
        )}
      </main>

      <ShiftDrawer
        open={drawer.open}
        shift={drawer.shift}
        prefill={drawer.prefill}
        clinicians={clinicians}
        today={today}
        onClose={() => setDrawer({ open: false, shift: null })}
        onSaved={(r) => {
          const p = r.shifts[0]?.providerId;
          if (p) setLastProvider(p);
          setDrawer({ open: false, shift: null });
          refresh();
        }}
        onCancelShift={(s) => setCancelTarget(s)}
      />

      <CancelShiftDialog
        open={Boolean(cancelTarget)}
        summary={cancelTarget ? `${cancelTarget.patient?.name || 'Shift'} · ${dayLabel(cancelTarget.date)} ${shiftWindow(cancelTarget, true)}` : ''}
        allowFollowing={Boolean(cancelTarget?.isRecurring)}
        busy={cancelBusy}
        onCancel={() => setCancelTarget(null)}
        onConfirm={async (code, note, scope) => {
          if (!cancelTarget) return;
          setCancelBusy(true);
          try {
            const done = await ScheduleService.cancel(cancelTarget.id, code, note, scope);
            showToast(`Cancelled ${done.length} shift${done.length === 1 ? '' : 's'}`, { variant: 'success' });
            setCancelTarget(null);
            setDrawer({ open: false, shift: null });
            refresh();
          } catch (e) {
            showToast(scheduleErrorMessage(e, 'Could not cancel the shift'), { variant: 'error' });
          } finally {
            setCancelBusy(false);
          }
        }}
      />

      <CopyWeekDialog open={copyOpen} targetWeek={range.from} onClose={() => setCopyOpen(false)} onDone={() => { setCopyOpen(false); refresh(); }} />
    </div>
  );
};

/** Copy last week's shifts into this week (one-off copies). Conflicts are skipped and listed. */
const CopyWeekDialog: React.FC<{ open: boolean; targetWeek: string; onClose: () => void; onDone: () => void }> = ({ open, targetWeek, onClose, onDone }) => {
  const { showToast } = useToast();
  const [preview, setPreview] = useState<{ count: number; conflicts: number } | null>(null);
  const [busy, setBusy] = useState(false);
  const source = addDays(targetWeek, -7);
  useEffect(() => {
    if (!open) return;
    setPreview(null);
    ScheduleService.bulk('copyWeek', { sourceWeekStart: source, targetFrom: targetWeek }, { dryRun: true })
      .then((r) => setPreview({ count: r.count ?? 0, conflicts: r.conflicts?.length ?? 0 }))
      .catch((e) => showToast(scheduleErrorMessage(e, 'Could not check last week'), { variant: 'error' }));
  }, [open, source, targetWeek, showToast]);
  return (
    <Dialog open={open} onClose={onClose} labelledBy="copy-week-title">
      <h2 id="copy-week-title" className="text-lg font-semibold text-gray-900">Copy last week</h2>
      <p className="mt-1 text-sm text-gray-600">Copies the shifts from {rangeLabel(source, addDays(source, 6))} into {rangeLabel(targetWeek, addDays(targetWeek, 6))}, with the same clinicians and times.</p>
      <p className="mt-3 text-sm text-gray-800">
        {preview ? <>{preview.count} shift{preview.count === 1 ? '' : 's'} will be created{preview.conflicts ? `; ${preview.conflicts} skipped because they would overlap an existing shift or the patient can't be scheduled` : ''}.</> : 'Checking…'}
      </p>
      <div className="mt-6 flex justify-end gap-2">
        <button type="button" onClick={onClose} className="min-h-[44px] rounded-md border border-gray-300 px-4 text-sm">Close</button>
        <button
          type="button"
          disabled={!preview || preview.count === 0 || busy}
          onClick={async () => {
            setBusy(true);
            try {
              const r = await ScheduleService.bulk('copyWeek', { sourceWeekStart: source, targetFrom: targetWeek });
              showToast(`Copied ${r.created ?? 0} shift(s)`, { variant: 'success' });
              onDone();
            } catch (e) {
              showToast(scheduleErrorMessage(e, 'Copy failed'), { variant: 'error' });
            } finally {
              setBusy(false);
            }
          }}
          className="min-h-[44px] rounded-md bg-slate-800 px-4 text-sm font-semibold text-white disabled:opacity-50"
        >
          {busy ? 'Copying…' : 'Copy shifts'}
        </button>
      </div>
    </Dialog>
  );
};

export default ScheduleShell;
